import {
  type FeeDragAssumptions,
  type FeeDragInput,
  FeeDragInputSchema,
  type LikelihoodBand,
  type LikelihoodInput,
  LikelihoodInputSchema,
  type LostSuperLikelihoodOutput,
  type LostSuperRuleSet,
  type MultipleAccountFeeDragOutput,
} from "./types.js";

// ---------------------------------------------------------------------------
// HARD RULE (binding, panel C2 / contra-regulatory §C2): superannuation is an
// s764A financial product with NO carve-out. No surface that displays this
// tool's personalised output may name, rank, link, or imply a DESTINATION super
// fund to consolidate into — that is the INFO 269 "dealing by arranging" /
// general-advice kill-line. Every CTA routes only to ATO / myGov. Affiliate
// revenue is $0 by design.
//
// We encode that as a guard run over every emitted output object, NOT as doc
// prose. Any string field that smells like a destination-fund handoff (a product
// identifier, a "switch/join/open an account" CTA, an affiliate/sponsored tile,
// or a "best/low-fee fund" steer) trips the guard and the calc throws — so a
// casual later "add a Fund X consolidation tile to the result" becomes a loud
// test failure, not a silent §C2 breach.
//
// Note: this tool legitimately explains the ATO's own *neutral* mechanic ("lost
// super can be fund-held or ATO-held"; "you can ask the ATO to transfer ATO-held
// super to an eligible fund"). That descriptive use of the bare word "fund" is
// fine and must keep working — so the guard matches HANDOFF tokens (product IDs +
// action CTAs + steering adjectives), not the word "fund" on its own.
// ---------------------------------------------------------------------------

/**
 * Tokens that signal a DESTINATION-fund handoff has leaked into a personalised
 * result. Deliberately excludes the bare word "fund" (the ATO's neutral mechanic
 * uses it). Matches product identifiers, sign-up / switch CTAs, paid-placement
 * markers, and "best/cheapest/low-fee fund" steers.
 */
export const FUND_HANDOFF_TOKENS = [
  // Super-product / fund identifiers — naming a specific destination product.
  "usi", // unique superannuation identifier
  "spin", // superannuation product identification number
  "abn:",
  "smsf",
  // Action CTAs that arrange a deal into a destination fund.
  "open an account",
  "open a super account",
  "sign up",
  "join now",
  "join this fund",
  "switch to",
  "switch your super to",
  "rollover to",
  "roll your super into",
  "consolidate into ",
  "move your super to",
  "apply now",
  // Paid-placement / affiliate markers.
  "affiliate",
  "sponsored",
  "partner offer",
  "referral link",
  // Comparison / ranking / steering — implying a destination product.
  "compare funds",
  "best super fund",
  "best fund",
  "top super fund",
  "low-fee fund",
  "lowest-fee fund",
  "cheapest fund",
  "recommended fund",
] as const;

/**
 * Guard: assert no destination-fund handoff can reach a personalised-result
 * output. Walks every string value in the output object and throws if any
 * handoff token matches. Pure; returns the output unchanged when clean so it can
 * wrap a `return`.
 */
export function assertNoFundHandoff<T>(output: T): T {
  const visit = (value: unknown): void => {
    if (typeof value === "string") {
      const haystack = value.toLowerCase();
      for (const token of FUND_HANDOFF_TOKENS) {
        if (haystack.includes(token)) {
          throw new Error(
            `lost-super: blocked destination-fund handoff in a personalised result (matched "${token}"). Superannuation is an s764A financial product — every CTA must route to ATO/myGov, never a fund (panel C2).`,
          );
        }
      }
      return;
    }
    if (Array.isArray(value)) {
      for (const item of value) visit(item);
      return;
    }
    if (value && typeof value === "object") {
      for (const v of Object.values(value)) visit(v);
    }
  };
  visit(output);
  return output;
}

// ---------------------------------------------------------------------------
// Surface 1 — lostSuperLikelihood (triage)
// ---------------------------------------------------------------------------

/**
 * Coarse risk weights for the triage decision tree. Each signal that makes
 * detached / ATO-held super more likely adds points; having already consolidated
 * subtracts. These are a literacy heuristic, not a calculation of a dollar — the
 * output is a hedged typical-case band, never a finding about the user.
 */
const SCORE_WEIGHTS = {
  /** Each employer beyond the first risks a default account left behind. */
  perEmployerOverOne: 1,
  /** Each home move risks lost mail → an account going inactive. */
  perAddressChange: 1,
  /** A legal name change breaks fund-side matching. */
  nameChanged: 1,
  /** A known dormant account is a direct signal. */
  hasDormantAccount: 2,
  /** Overseas work commonly strands an Australian account. */
  workedOverseas: 1,
  /** Having already consolidated/checked materially lowers the likelihood. */
  hasConsolidatedBefore: -2,
} as const;

/** Score the coarse triage signals into a single risk score (floored at 0). */
function scoreLikelihood(input: LikelihoodInput): number {
  const employersOverOne = Math.max(0, input.employerCount - 1);
  const raw =
    employersOverOne * SCORE_WEIGHTS.perEmployerOverOne +
    input.addressChanges * SCORE_WEIGHTS.perAddressChange +
    (input.nameChanged ? SCORE_WEIGHTS.nameChanged : 0) +
    (input.hasDormantAccount ? SCORE_WEIGHTS.hasDormantAccount : 0) +
    (input.workedOverseas ? SCORE_WEIGHTS.workedOverseas : 0) +
    (input.hasConsolidatedBefore ? SCORE_WEIGHTS.hasConsolidatedBefore : 0);
  return Math.max(0, raw);
}

/** Pick the band whose `[minScore, maxScore)` contains `score`. */
function bandFor(
  bands: readonly LikelihoodBand[],
  score: number,
): LikelihoodBand {
  for (const band of bands) {
    if (
      score >= band.minScore &&
      (band.maxScore === null || score < band.maxScore)
    ) {
      return band;
    }
  }
  // Bands are authored to cover [0, ∞); a miss means a malformed ruleset.
  throw new Error(`no lost-super band matched score ${score}`);
}

/**
 * Triage the likelihood of lost / ATO-held super from coarse, non-identifying
 * history. Pure: same inputs + ruleset always yield the same output. The output
 * is a hedged likelihood band + the ATO population prize, and a FIXED free action
 * routing to ATO/myGov — never a fund. Runs through the no-handoff guard.
 */
export function lostSuperLikelihood(
  rawInput: LikelihoodInput,
  rules: LostSuperRuleSet,
): LostSuperLikelihoodOutput {
  const input = LikelihoodInputSchema.parse(rawInput);
  const score = scoreLikelihood(input);
  const band = bandFor(rules.bands, score);

  return assertNoFundHandoff({
    score,
    bandKey: band.key,
    bandLabel: band.label,
    prize: rules.prize,
    freeAction: {
      // ATO's neutral phrasing — describe the free government action, full stop.
      label:
        "Sign in to myGov and check the ATO's record of your super, including any lost or ATO-held amounts, then consolidate it yourself if you choose to (check insurance and exit fees first).",
      destination: "myGov / ATO online services",
    },
  });
}

// ---------------------------------------------------------------------------
// Surface 2 — multipleAccountFeeDrag (duplicate-cost illustration)
// ---------------------------------------------------------------------------

/** Merge caller assumption overrides onto the ruleset defaults. */
function resolveAssumptions(
  input: FeeDragInput,
  defaults: FeeDragAssumptions,
): FeeDragAssumptions {
  const o = input.assumptions ?? {};
  return {
    annualReturn: o.annualReturn ?? defaults.annualReturn,
    adminFeePerAccount: o.adminFeePerAccount ?? defaults.adminFeePerAccount,
    insurancePremiumPerAccount:
      o.insurancePremiumPerAccount ?? defaults.insurancePremiumPerAccount,
  };
}

/**
 * Future value of a series of `annual` end-of-year contributions compounded at
 * `rate` for `years` (ordinary-annuity FV). At rate 0 this is `annual × years`.
 *
 *   FV = annual × ((1 + r)^years − 1) / r
 */
function futureValueAnnuity(
  annual: number,
  rate: number,
  years: number,
): number {
  if (years <= 0) return 0;
  if (rate === 0) return annual * years;
  return (annual * (Math.pow(1 + rate, years) - 1)) / rate;
}

/**
 * Quantify the recurring duplicate admin-fee + insurance-premium leak of holding
 * 2+ super accounts, compounded to retirement. A transparent illustration with
 * disclosed assumptions — framed as a DUPLICATE-COST illustration, never a
 * prediction. Pure: `inputs → rules → outputs`. Runs through the no-handoff
 * guard (the illustration must never route to a destination fund either).
 */
export function multipleAccountFeeDrag(
  rawInput: FeeDragInput,
  rules: LostSuperRuleSet,
): MultipleAccountFeeDragOutput {
  const input = FeeDragInputSchema.parse(rawInput);
  const assumptions = resolveAssumptions(input, rules.feeDragDefaults);

  // Only the DUPLICATE accounts leak — the primary account is necessary.
  const duplicateAccounts = Math.max(0, input.accountCount - 1);

  const perDuplicateAnnual =
    assumptions.adminFeePerAccount +
    (input.duplicateInsurance ? assumptions.insurancePremiumPerAccount : 0);
  const annualDuplicateCost = duplicateAccounts * perDuplicateAnnual;

  const totalFeesPaid = annualDuplicateCost * input.yearsToRetirement;
  const compoundedToRetirement = futureValueAnnuity(
    annualDuplicateCost,
    assumptions.annualReturn,
    input.yearsToRetirement,
  );

  return assertNoFundHandoff({
    duplicateAccounts,
    annualDuplicateCost,
    yearsToRetirement: input.yearsToRetirement,
    totalFeesPaid: Math.round(totalFeesPaid),
    compoundedToRetirement: Math.round(compoundedToRetirement),
    assumptionsUsed: {
      ...assumptions,
      duplicateInsurance: input.duplicateInsurance,
    },
  });
}
